'use client';

import { usePageAction } from '@ria-client/hooks/usePageAction';
import { useOptionalAnalyticsPageView } from '@ria-client/segment/Analytics/AnalyticsPageView';
import { useNWAElementImpression } from '@ria-client/segment/hooks/useNWAElementImpression';
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type MouseEvent,
} from 'react';

export type ContentLinkProps = ComponentProps<'a'>;

/**
 * Drop-in <a> that emits the same Link Viewed / Link Clicked pair as seo-pages' Link.astro
 * (entity_name "link"). These feed SEO_MART link metrics and the WP Metrics Overlay pills.
 */
export function ContentLink(props: ContentLinkProps) {
  // ponytail: outside an AnalyticsPageView (unit tests, non-page consumers), render untracked instead of throwing.
  return useOptionalAnalyticsPageView() ? (
    <TrackedAnchor key={props.href} {...props} />
  ) : (
    <a {...props} />
  );
}

function TrackedAnchor({ href, onClick, ref, children, ...rest }: ContentLinkProps) {
  const anchorRef = useRef<HTMLAnchorElement | null>(null);
  const [linkCopy, setLinkCopy] = useState<string>();

  useLayoutEffect(() => {
    const el = anchorRef.current;
    setLinkCopy(el ? visibleLinkCopy(el) : undefined);
  }, [children]);

  const {
    ref: viewRef,
    impressionId,
    ensureViewed,
  } = useNWAElementImpression({
    component: 'link',
    destinationUrl: href,
    linkCopy,
  });
  const fireClick = usePageAction({
    actionName: 'link',
    actionSection: 'content',
    actionDestinationUrl: href,
    linkCopy,
    parentEventId: impressionId,
  });

  const setRefs = useCallback(
    (node: HTMLAnchorElement | null) => {
      anchorRef.current = node;
      viewRef(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [viewRef, ref],
  );

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    ensureViewed();
    fireClick();
    onClick?.(event);
  };

  return (
    <a {...rest} href={href} ref={setRefs} onClick={handleClick}>
      {children}
    </a>
  );
}

/**
 * seo-pages parity: link_copy = el.innerText (keeps CSS text-transform). innerText still includes
 * visually-hidden .sr-only copy, so hide it for the read. jsdom has no innerText; fall back to textContent.
 */
function visibleLinkCopy(el: HTMLElement): string | undefined {
  const srOnly = Array.from(el.querySelectorAll<HTMLElement>('.sr-only'));
  srOnly.forEach(node => node.style.setProperty('display', 'none'));
  const text = el.innerText ?? el.textContent ?? '';
  srOnly.forEach(node => node.style.removeProperty('display'));
  return text.replace(/\s+/g, ' ').trim() || undefined;
}
